import { api, ApiError, type InfoShareCreateDefaults } from '../api/client';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { createTextInfoShare, parseReplyOptionsFromForm, type InfoSharePasswordMode } from '../crypto/infoShareCreate';
import { resolvePasswordPolicy } from '../crypto/passwordPoliciesApi';
import { defaultPolicyForTarget } from '../crypto/passwordPolicy';
import { bindPhoneInputNormalize, isValidE164Phone, normalizeToE164 } from '../crypto/smsPassword';
import { loadVaultSession } from '../crypto/vaultSession';
import { showShareLinkDialog } from '../ui/shareLinkDialog';
import {
  bindPasswordPolicyToForm,
  passwordPolicyErrorFromCause,
  validatePasswordForUi,
} from '../ui/passwordPolicyUi';
import {
  openInfoShareManageDrawer,
  syncReplyOptionsVisibility,
  DEFAULT_MAX_REPLY_FILES,
  DEFAULT_MAX_REPLY_MB,
} from '../ui/infoShareManage';
import { openInfoShareRepliesDrawer } from '../ui/infoShareReplies';
import { confirmRevokeShare } from '../ui/confirmDialog';
import { InfoShareListController } from '../ui/infoShareList';
import { connectInfoShareLive, disconnectInfoShareLive, patchShareFromSummary } from '../ws/infoShareLive';
import {
  connectSmsDeliveryLive,
  disconnectSmsDeliveryLive,
  holdSmsDeliveryLive,
  isSmsDeliveryLiveActive,
  smsDeliveryUpdateAffectsFailureBanner,
  type SmsDeliveryUpdatedMessage,
} from '../ws/smsDeliveryLive';
import { iconImg } from '../ui/icons';
import { bindMessageInputSizing } from '../ui/messageInput';
import { createHelpTip } from '../help/helpTips';
import { consumeCreateIntent } from '../app/navigationState';
import { mountInfoShareAttachmentField } from '../ui/infoShareAttachmentField';
import { setCryptoBusyProgress } from '../ui/cryptoBusy';
import { MAX_RELATIVE_EXPIRY_DAYS } from '../crypto/infoShareLimits';

const BYTES_PER_MB = 1024 * 1024;
const DEFAULT_CREATE_DEFAULTS: InfoShareCreateDefaults = {
  expiresDays: 3,
  multiUseAccess: false,
  passwordMode: 'None',
  allowGuestReply: false,
  replyAllowsFiles: false,
  maxReplyFiles: DEFAULT_MAX_REPLY_FILES,
  maxReplyTotalBytes: DEFAULT_MAX_REPLY_MB * BYTES_PER_MB,
  replyRetentionDaysAfterOpen: 7,
};

function formatDateTime(value: Date): string {
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}`;
}

function resolveExpiresAt(form: HTMLFormElement): Date | null {
  const data = new FormData(form);
  const mode = String(data.get('expiryMode') ?? 'relative');

  if (mode === 'absolute') {
    const expiresAt = new Date(String(data.get('expiresAt')));
    return Number.isNaN(expiresAt.getTime()) ? null : expiresAt;
  }

  const days = Number.parseInt(String(data.get('expiresDays') ?? ''), 10);
  if (!Number.isFinite(days) || days < 1) {
    return null;
  }

  return new Date(Date.now() + days * 24 * 60 * 60 * 1000);
}

function syncExpiryInputs(form: HTMLFormElement): void {
  const mode = String(new FormData(form).get('expiryMode') ?? 'relative');
  const useRelative = mode === 'relative';
  const daysInput = form.querySelector('input[name="expiresDays"]') as HTMLInputElement;
  const expiresAtInput = form.querySelector('input[name="expiresAt"]') as HTMLInputElement;
  daysInput.disabled = !useRelative;
  expiresAtInput.disabled = useRelative;
  daysInput.required = useRelative;
  expiresAtInput.required = !useRelative;
}

export function InformationPage(): HTMLElement {
  const page = document.createElement('section');
  page.className = 'module-page module-page--info';

  page.innerHTML = `
    <div class="panel-grid panel-grid--info">
      <section class="module-section info-create-section">
        <h2 data-i18n="info.owner.createTitle"></h2>
        <p class="muted" data-i18n="info.owner.createSubtitle"></p>
        <form class="form-grid info-create-form" id="info-create-form" method="post" action="#">
          <label>
            <span data-i18n="info.owner.label"></span>
            <input type="text" name="label" maxlength="200" autocomplete="off" />
            <span class="muted" data-i18n="info.owner.labelHint"></span>
          </label>
          <label class="message-field">
            <span data-i18n="info.owner.message"></span>
            <textarea class="message-input" name="text" required></textarea>
          </label>
          <div id="info-create-attachments"></div>
          <fieldset class="expiry-fieldset">
            <label class="expiry-option">
              <input type="radio" name="expiryMode" value="relative" checked />
              <span data-i18n="info.owner.expiresIn"></span>
              <input type="number" name="expiresDays" min="1" max="${MAX_RELATIVE_EXPIRY_DAYS}" value="3" required />
              <span data-i18n="info.owner.expiresDays"></span>
            </label>
            <label class="expiry-option">
              <input type="radio" name="expiryMode" value="absolute" />
              <span data-i18n="info.owner.expiresOn"></span>
              <input type="datetime-local" name="expiresAt" disabled />
            </label>
          </fieldset>
          <label class="checkbox-row">
            <input type="checkbox" name="multiUseAccess" />
            <span data-i18n="info.owner.multiUse"></span>
          </label>
          <div class="warning-panel" id="info-create-multi-use-warning" hidden>
            <span class="warning-panel__icon"></span>
            <span data-i18n="info.owner.multiUseCreateWarning"></span>
          </div>
          <fieldset class="password-mode-fieldset">
            <legend data-i18n="info.owner.passwordMode"></legend>
            <label class="checkbox-row">
              <input type="radio" name="passwordMode" value="none" checked />
              <span data-i18n="info.owner.passwordModeNone"></span>
            </label>
            <label class="checkbox-row">
              <input type="radio" name="passwordMode" value="userSet" />
              <span data-i18n="info.owner.passwordModeUserSet"></span>
            </label>
            <label class="checkbox-row">
              <input type="radio" name="passwordMode" value="sms" />
              <span data-i18n="info.owner.passwordModeSms"></span>
            </label>
          </fieldset>
          <div id="owner-password-group" class="password-fields" hidden>
            <label class="guest-password-field">
              <span data-i18n="info.owner.password"></span>
              <input type="password" name="password" minlength="8" autocomplete="new-password" />
            </label>
            <label class="guest-password-field">
              <span data-i18n="info.owner.passwordConfirm"></span>
              <input type="password" name="passwordConfirm" minlength="8" autocomplete="new-password" />
            </label>
            <p class="muted password-fields__hint" id="info-password-policy-hint" data-i18n="info.owner.passwordHint"></p>
          </div>
          <div id="owner-sms-phone-group" class="password-fields" hidden>
            <label class="guest-password-field">
              <span data-i18n="info.owner.smsPhone"></span>
              <input type="tel" name="smsPhoneNumber" autocomplete="tel" placeholder="+31612345678" />
              <span class="muted" data-i18n="info.owner.smsPhoneHint"></span>
            </label>
          </div>
          <div class="warning-panel" id="info-create-password-warning" hidden>
            <span class="warning-panel__icon"></span>
            <span data-i18n="info.owner.passwordChannelWarning"></span>
          </div>
          <label class="checkbox-row">
            <input type="checkbox" name="allowGuestReply" />
            <span data-i18n="info.owner.allowGuestReply"></span>
          </label>
          <div id="reply-options" class="reply-options" hidden>
            <label class="checkbox-row">
              <input type="checkbox" name="replyAllowsFiles" />
              <span data-i18n="info.owner.replyAllowsFiles"></span>
            </label>
            <div id="reply-file-limits" class="reply-file-limits" hidden>
              <label class="expiry-option">
                <span data-i18n="info.owner.maxReplyFiles"></span>
                <input type="number" name="maxReplyFiles" min="1" max="1000" value="${DEFAULT_MAX_REPLY_FILES}" />
              </label>
              <label class="expiry-option">
                <span data-i18n="info.owner.maxReplyTotalMb"></span>
                <input type="number" name="maxReplyTotalMb" min="1" max="10240" value="${DEFAULT_MAX_REPLY_MB}" />
                <span data-i18n="info.owner.maxReplyMbUnit"></span>
              </label>
            </div>
          </div>
          <button type="submit" class="button primary">
            <span class="button__icon"></span>
            <span data-i18n="info.owner.createSubmit"></span>
          </button>
        </form>
        <p class="error-text" id="info-create-error" hidden></p>
      </section>
      <section class="module-section info-share-list-section">
        <div class="module-section__header module-section__header--split">
          <div class="module-section__header-main">
            <h2 data-i18n="info.owner.listTitle"></h2>
            <span class="live-indicator" id="info-live-indicator" hidden aria-live="polite"></span>
          </div>
          <label class="checkbox-row info-share-list-filter">
            <input type="checkbox" id="info-hide-terminal" />
            <span data-i18n="info.owner.hideTerminal"></span>
          </label>
        </div>
        <p id="info-share-list-empty" class="muted" hidden></p>
        <div id="info-share-list" class="share-list"></div>
      </section>
    </div>
  `;

  page.querySelector('.button__icon')?.append(iconImg('sealed', 18));

  const form = page.querySelector('#info-create-form') as HTMLFormElement;
  const error = page.querySelector('#info-create-error') as HTMLParagraphElement;
  const list = page.querySelector('#info-share-list') as HTMLDivElement;
  const liveIndicator = page.querySelector('#info-live-indicator') as HTMLSpanElement;
  const expiresAtInput = form.querySelector('input[name="expiresAt"]') as HTMLInputElement;
  const ownerPasswordGroup = form.querySelector('#owner-password-group') as HTMLDivElement;
  const ownerSmsPhoneGroup = form.querySelector('#owner-sms-phone-group') as HTMLDivElement;
  const ownerPasswordInput = form.querySelector('input[name="password"]') as HTMLInputElement;
  const ownerPasswordConfirmInput = form.querySelector('input[name="passwordConfirm"]') as HTMLInputElement;
  const infoPasswordPolicyHint = form.querySelector('#info-password-policy-hint') as HTMLElement;
  let infoPasswordPolicy = defaultPolicyForTarget('infoSharePassword');
  void resolvePasswordPolicy('infoSharePassword').then((policy) => {
    infoPasswordPolicy = policy;
    bindPasswordPolicyToForm({
      policy,
      inputs: [ownerPasswordInput, ownerPasswordConfirmInput],
      hint: infoPasswordPolicyHint,
    });
  });
  const smsPhoneInput = form.querySelector('input[name="smsPhoneNumber"]') as HTMLInputElement;
  bindPhoneInputNormalize(smsPhoneInput);
  const multiUseWarning = form.querySelector('#info-create-multi-use-warning') as HTMLDivElement;
  const passwordChannelWarning = form.querySelector('#info-create-password-warning') as HTMLDivElement;
  const hideTerminalToggle = page.querySelector('#info-hide-terminal') as HTMLInputElement;
  const listEmpty = page.querySelector('#info-share-list-empty') as HTMLParagraphElement;

  for (const panel of [multiUseWarning, passwordChannelWarning]) {
    panel.querySelector('.warning-panel__icon')?.append(iconImg('warning', 20));
  }

  const defaultExpiry = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);
  expiresAtInput.value = formatDateTime(defaultExpiry);
  syncExpiryInputs(form);

  let ownerUserId = '';
  let createErrorKey: string | null = null;
  let liveConnected = false;

  const refreshLocale = () => {
    if (createErrorKey && !error.hidden) {
      error.textContent = t(createErrorKey);
    }
    if (liveConnected) {
      liveIndicator.textContent = t('info.owner.liveConnected');
    }
    listController.refreshLocale();
  };

  bindLocaleRefresh(refreshLocale);

  const syncSmsDeliveryLive = (): void => {
    if (listController.hasActiveSmsDeliveryFailures()) {
      if (isSmsDeliveryLiveActive()) {
        return;
      }
      connectSmsDeliveryLive({
        onUpdate: (message) => handleSmsDeliveryUpdate(message),
      });
      return;
    }

    // Soft disconnect — no-op while the share-link dialog holds the live socket.
    disconnectSmsDeliveryLive();
  };

  const handleSmsDeliveryUpdate = (message: SmsDeliveryUpdatedMessage): void => {
    const shareId = listController.findShareIdBySmsDeliveryReference(message.reference);
    if (!shareId) {
      return;
    }

    if (message.status === 'failed') {
      listController.patchSmsDeliveryFailure(shareId, {
        smsDeliveryStatus: 'failed',
        smsDeliveryErrorDescription: message.errorDescription,
      });
    } else if (message.status === 'delivered') {
      listController.patchSmsDeliveryFailure(shareId, {
        smsDeliveryStatus: null,
        smsDeliveryErrorDescription: null,
      });
    }

    // accepted/pending must not tear down a dialog-owned pending watch.
    if (smsDeliveryUpdateAffectsFailureBanner(message.status)) {
      syncSmsDeliveryLive();
    }
  };

  const listController = new InfoShareListController(list, listEmpty, {
    onOpenReplies: (shareId) => {
      void openInfoShareRepliesDrawer(shareId, ownerUserId, (id, unreadCount) => {
        listController.patchUnreadCount(id, unreadCount);
      });
    },
    onManage: (shareId) => {
      void openInfoShareManageDrawer(shareId, async () => {
        const detail = await api.getInfoShare(shareId);
        const previous = listController.getShareState(shareId);
        patchShareFromSummary({
          id: detail.id,
          serverToken: detail.serverToken,
          displayStatus: detail.displayStatus,
          createdAt: detail.createdAt,
          expiresAt: detail.expiresAt,
          multiUseAccess: detail.multiUseAccess,
          allowGuestReply: detail.allowGuestReply,
          passwordMode: detail.passwordMode,
          unreadReplyCount: previous?.unreadReplyCount ?? 0,
          labelBlobId: detail.labelBlobId,
          wasOpened: detail.wasOpened,
          smsDeliveryReference: previous?.smsDeliveryReference ?? null,
          smsDeliveryStatus: previous?.smsDeliveryStatus ?? null,
          smsDeliveryErrorDescription: previous?.smsDeliveryErrorDescription ?? null,
        });
      });
    },
    onDismissSmsDeliveryFailure: (shareId, reference) => {
      void (async () => {
        try {
          await api.dismissSmsDeliveryFailure(reference);
          listController.patchSmsDeliveryFailure(shareId, {
            smsDeliveryStatus: null,
            smsDeliveryErrorDescription: null,
          });
          syncSmsDeliveryLive();
        } catch {
          // keep failure visible
        }
      })();
    },
    onRevoke: (shareId) => {
      void (async () => {
        if (!(await confirmRevokeShare())) {
          return;
        }

        await api.revokeInfoShare(shareId);
        const state = listController.getShareState(shareId);
        if (state) {
          patchShareFromSummary({
            id: shareId,
            serverToken: '',
            displayStatus: 'Revoked',
            createdAt: '',
            expiresAt: state.expiresAt,
            multiUseAccess: state.multiUseAccess,
            allowGuestReply: state.allowGuestReply,
            passwordMode: '',
            unreadReplyCount: state.unreadReplyCount,
            labelBlobId: state.labelBlobId,
            wasOpened: state.wasOpened,
            smsDeliveryReference: state.smsDeliveryReference,
            smsDeliveryStatus: state.smsDeliveryStatus,
            smsDeliveryErrorDescription: state.smsDeliveryErrorDescription,
          });
        }
      })();
    },
  });

  function getPasswordMode(): InfoSharePasswordMode {
    const value = String(new FormData(form).get('passwordMode') ?? 'none');
    switch (value) {
      case 'userSet':
        return 'UserSet';
      case 'sms':
        return 'Sms';
      default:
        return 'None';
    }
  }

  function passwordModeToFormValue(mode: string): string {
    switch (mode) {
      case 'UserSet':
        return 'userSet';
      case 'Sms':
        return 'sms';
      default:
        return 'none';
    }
  }

  function applyCreateDefaults(defaults: InfoShareCreateDefaults): void {
    const daysInput = form.querySelector('input[name="expiresDays"]') as HTMLInputElement;
    daysInput.value = String(Math.min(MAX_RELATIVE_EXPIRY_DAYS, Math.max(1, defaults.expiresDays || 3)));
    (form.querySelector('input[name="expiryMode"][value="relative"]') as HTMLInputElement).checked = true;
    expiresAtInput.value = formatDateTime(new Date(Date.now() + Number(daysInput.value) * 24 * 60 * 60 * 1000));
    syncExpiryInputs(form);

    (form.querySelector('input[name="multiUseAccess"]') as HTMLInputElement).checked = defaults.multiUseAccess;

    const passwordValue = passwordModeToFormValue(defaults.passwordMode);
    const passwordRadio = form.querySelector(`input[name="passwordMode"][value="${passwordValue}"]`) as HTMLInputElement | null;
    if (passwordRadio) {
      passwordRadio.checked = true;
    }

    (form.querySelector('input[name="allowGuestReply"]') as HTMLInputElement).checked = defaults.allowGuestReply;
    (form.querySelector('input[name="replyAllowsFiles"]') as HTMLInputElement).checked =
      defaults.allowGuestReply && defaults.replyAllowsFiles;

    const maxFilesInput = form.querySelector('input[name="maxReplyFiles"]') as HTMLInputElement;
    const maxMbInput = form.querySelector('input[name="maxReplyTotalMb"]') as HTMLInputElement;
    maxFilesInput.value = String(defaults.maxReplyFiles ?? DEFAULT_MAX_REPLY_FILES);
    maxMbInput.value = String(
      defaults.maxReplyTotalBytes != null
        ? Math.max(1, Math.round(defaults.maxReplyTotalBytes / BYTES_PER_MB))
        : DEFAULT_MAX_REPLY_MB,
    );

    syncReplyOptionsVisibility(form);
    syncPasswordModeFields();
  }

  function collectCreateDefaults(): InfoShareCreateDefaults {
    const replyOptions = parseReplyOptionsFromForm(form);
    const expiresAt = resolveExpiresAt(form);
    const fromAbsolute = expiresAt
      ? Math.ceil((expiresAt.getTime() - Date.now()) / (24 * 60 * 60 * 1000))
      : NaN;
    const daysRaw = Number.parseInt(String(new FormData(form).get('expiresDays') ?? '3'), 10);
    const expiresDays = Number.isFinite(fromAbsolute) && fromAbsolute >= 1
      ? Math.min(MAX_RELATIVE_EXPIRY_DAYS, fromAbsolute)
      : Number.isFinite(daysRaw) && daysRaw >= 1
        ? daysRaw
        : 3;
    return {
      expiresDays,
      multiUseAccess: (form.querySelector('input[name="multiUseAccess"]') as HTMLInputElement).checked,
      passwordMode: getPasswordMode(),
      allowGuestReply: replyOptions.allowGuestReply,
      replyAllowsFiles: replyOptions.replyAllowsFiles,
      maxReplyFiles: replyOptions.maxReplyFiles ?? DEFAULT_MAX_REPLY_FILES,
      maxReplyTotalBytes: replyOptions.maxReplyTotalBytes ?? DEFAULT_MAX_REPLY_MB * BYTES_PER_MB,
      replyRetentionDaysAfterOpen: 7,
    };
  }

  function syncCreateWarnings(): void {
    const multiUseAccess = (form.querySelector('input[name="multiUseAccess"]') as HTMLInputElement).checked;
    multiUseWarning.hidden = !multiUseAccess;
    passwordChannelWarning.hidden = getPasswordMode() === 'None';
  }

  function syncPasswordModeFields(): void {
    const mode = getPasswordMode();
    ownerPasswordGroup.hidden = mode !== 'UserSet';
    ownerSmsPhoneGroup.hidden = mode !== 'Sms';
    ownerPasswordInput.required = mode === 'UserSet';
    ownerPasswordConfirmInput.required = mode === 'UserSet';
    smsPhoneInput.required = mode === 'Sms';

    if (mode !== 'UserSet') {
      ownerPasswordInput.value = '';
      ownerPasswordConfirmInput.value = '';
    }
    if (mode !== 'Sms') {
      smsPhoneInput.value = '';
    }
    syncCreateWarnings();
  }

  syncPasswordModeFields();
  syncCreateWarnings();

  const messageInput = form.querySelector('textarea[name="text"]') as HTMLTextAreaElement;
  bindMessageInputSizing(messageInput);
  const attachmentField = mountInfoShareAttachmentField(
    form.querySelector('#info-create-attachments') as HTMLElement,
  );

  form.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    if (target.name === 'expiryMode') {
      syncExpiryInputs(form);
    }

    if (target.name === 'allowGuestReply' || target.name === 'replyAllowsFiles') {
      syncReplyOptionsVisibility(form);
    }

    if (target.name === 'multiUseAccess') {
      syncCreateWarnings();
    }

    if (target.name === 'passwordMode') {
      syncPasswordModeFields();
    }
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      error.hidden = true;
      const text = String(new FormData(form).get('text') ?? '').trim();
      const label = String(new FormData(form).get('label') ?? '').trim();
      const expiresAt = resolveExpiresAt(form);
      const multiUseAccess = new FormData(form).get('multiUseAccess') === 'on';
      const passwordMode = getPasswordMode();
      const password = passwordMode === 'UserSet'
        ? String(new FormData(form).get('password') ?? '').trim()
        : '';
      const passwordConfirm = passwordMode === 'UserSet'
        ? String(new FormData(form).get('passwordConfirm') ?? '').trim()
        : '';
      const smsPhoneRaw = passwordMode === 'Sms'
        ? String(new FormData(form).get('smsPhoneNumber') ?? '').trim()
        : '';
      const smsPhoneNumber = passwordMode === 'Sms' ? normalizeToE164(smsPhoneRaw) : null;
      const replyOptions = parseReplyOptionsFromForm(form);

      if (!text || !expiresAt) {
        error.hidden = false;
        createErrorKey = 'status.error';
        error.textContent = t(createErrorKey);
        return;
      }

      if (passwordMode === 'UserSet' && password !== passwordConfirm) {
        error.hidden = false;
        createErrorKey = 'info.owner.passwordMismatch';
        error.textContent = t(createErrorKey);
        return;
      }

      if (passwordMode === 'UserSet') {
        const policyMessage = validatePasswordForUi(password, infoPasswordPolicy);
        if (policyMessage) {
          error.hidden = false;
          createErrorKey = null;
          error.textContent = policyMessage;
          return;
        }
      }

      if (passwordMode === 'Sms' && (!smsPhoneNumber || !isValidE164Phone(smsPhoneNumber))) {
        error.hidden = false;
        createErrorKey = 'info.owner.smsPhoneInvalid';
        error.textContent = t(createErrorKey);
        return;
      }

      if (passwordMode === 'Sms' && !loadVaultSession()) {
        error.hidden = false;
        createErrorKey = 'info.owner.smsVaultRequired';
        error.textContent = t(createErrorKey);
        return;
      }

      if (smsPhoneNumber) {
        smsPhoneInput.value = smsPhoneNumber;
      }

      const session = await api.getSession();
      const files = attachmentField.getFiles();
      const payload = {
        text,
        label: label || undefined,
        files: files.length > 0 ? files : undefined,
        expiresAt,
        multiUseAccess,
        userId: session.userId,
        passwordMode,
        password: passwordMode === 'UserSet' ? password : undefined,
        smsPhoneNumber: passwordMode === 'Sms' ? smsPhoneNumber ?? undefined : undefined,
        ...replyOptions,
      };

      await showShareLinkDialog(
        async (hooks) => {
          try {
            const dialogBody = document.querySelector('.modal-dialog .crypto-busy-panel')?.closest('.modal-body')
              ?? document.querySelector('.modal-dialog');
            const created = await createTextInfoShare({
              ...payload,
              onSmsDeliveryReferenceReady: hooks.onSmsDeliveryReferenceReady,
              onProgress: (progress) => {
                if (dialogBody instanceof HTMLElement) {
                  setCryptoBusyProgress(dialogBody, progress);
                }
              },
            });
            const settingsToSave = collectCreateDefaults();
            try {
              await api.putInfoShareCreateDefaults(settingsToSave);
            } catch (saveCause) {
              console.error('Save info-share create defaults failed', saveCause);
            }
            form.reset();
            attachmentField.clear();
            applyCreateDefaults(settingsToSave);
            bindMessageInputSizing(messageInput);
            return {
              guestUrl: created.guestUrl,
              shareId: created.shareId,
              multiUseAccess,
              smsDeliveryReference: created.smsDeliveryReference,
              smsPhoneNumber: payload.smsPhoneNumber,
            };
          } catch (cause) {
            const policyError = passwordPolicyErrorFromCause(cause, infoPasswordPolicy);
            if (policyError) {
              error.hidden = false;
              createErrorKey = null;
              error.textContent = policyError;
            } else if (cause instanceof Error && cause.message === 'vault_locked') {
              error.hidden = false;
              createErrorKey = 'info.owner.smsVaultRequired';
              error.textContent = t(createErrorKey);
            } else if (cause instanceof ApiError && (
              cause.code === 'sms_send_failed'
              || cause.code === 'sms_not_configured'
              || cause.code === 'sms_provider_error'
              || cause.code === 'invalid_phone_number'
              || cause.code === 'destination_ciphertext_required'
              || cause.code === 'destination_ciphertext_invalid'
              || cause.code === 'sms_reference_invalid'
              || cause.code === 'sms_reference_conflict'
            )) {
              error.hidden = false;
              createErrorKey = cause.code === 'invalid_phone_number'
                ? 'info.owner.smsPhoneInvalid'
                : cause.code === 'destination_ciphertext_required'
                  || cause.code === 'destination_ciphertext_invalid'
                  ? 'info.owner.smsVaultRequired'
                  : 'info.owner.smsSendFailed';
              error.textContent = t(createErrorKey);
            }
            throw cause;
          }
        },
        {
          onSmsDeliveryUpdate: handleSmsDeliveryUpdate,
        },
      );
      syncSmsDeliveryLive();
    })();
  });

  const HIDE_TERMINAL_KEY = 'shareweb:infoShareHideTerminal';
  hideTerminalToggle.checked = localStorage.getItem(HIDE_TERMINAL_KEY) === '1';
  listController.setHideTerminal(hideTerminalToggle.checked);
  hideTerminalToggle.addEventListener('change', () => {
    localStorage.setItem(HIDE_TERMINAL_KEY, hideTerminalToggle.checked ? '1' : '0');
    listController.setHideTerminal(hideTerminalToggle.checked);
  });

  async function loadInitialList(): Promise<void> {
    try {
      const [shares, session, defaults] = await Promise.all([
        api.listInfoShares(),
        api.getSession(),
        api.getInfoShareCreateDefaults().catch(() => DEFAULT_CREATE_DEFAULTS),
      ]);
      ownerUserId = session.userId;
      applyCreateDefaults(defaults);
      // Re-apply so hide preference is set before rows render (covers remount races).
      listController.setHideTerminal(hideTerminalToggle.checked);
      listController.setShares(shares);
      syncSmsDeliveryLive();
    } catch (cause) {
      console.error('List info shares failed', cause);
    }
  }

  void loadInitialList().then(() => {
    connectInfoShareLive(listController, {
      onStateChange: (state) => {
        liveConnected = state === 'connected';
        liveIndicator.hidden = !liveConnected;
        liveIndicator.textContent = liveConnected ? t('info.owner.liveConnected') : '';
      },
    });
  });

  const observer = new MutationObserver(() => {
    if (!page.isConnected) {
      attachmentField.dispose();
      listController.dispose();
      disconnectInfoShareLive();
      holdSmsDeliveryLive(false);
      disconnectSmsDeliveryLive({ force: true });
      observer.disconnect();
    }
  });
  // Observe the app root only (not subtree) so list row updates never trigger dispose.
  const appRoot = document.getElementById('app') ?? document.body;
  observer.observe(appRoot, { childList: true });

  applyI18n(page);
  page.querySelector('.info-create-section > h2')?.append(createHelpTip('information'));
  page.querySelector('.info-share-list-section .module-section__header-main h2')?.append(createHelpTip('information'));

  if (consumeCreateIntent()) {
    const createSection = page.querySelector('.info-create-section') as HTMLElement | null;
    const activateCreate = () => {
      if (!page.isConnected) {
        requestAnimationFrame(activateCreate);
        return;
      }
      createSection?.classList.add('info-create-section--focus');
      createSection?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      messageInput.focus({ preventScroll: true });
      window.setTimeout(() => createSection?.classList.remove('info-create-section--focus'), 1600);
    };
    requestAnimationFrame(activateCreate);
  }

  return page;
}

export function disposeInformationPageLive(): void {
  disconnectInfoShareLive();
  holdSmsDeliveryLive(false);
  disconnectSmsDeliveryLive({ force: true });
}
